/**
 * Tipos médicos core para el sistema Clinical Graph Triage
 * Define estructuras de datos para nodos clínicos, relaciones y pacientes
 */

import * as THREE from 'three';

// ═══════════════════════════════════════════════════════════════════════════════
// PRIORIDAD DE TRIAGE (Protocolos START/SALT)
// ═══════════════════════════════════════════════════════════════════════════════

export type TriagePriority = 'immediate' | 'delayed' | 'minor' | 'expectant' | 'deceased';

// ═══════════════════════════════════════════════════════════════════════════════
// TIPOS DE NODOS MÉDICOS (Categorización FHIR-inspired)
// ═══════════════════════════════════════════════════════════════════════════════

export type NodeType = 'vital_sign' | 'symptom' | 'physical_exam' | 'laboratory' | 'imaging' | 'diagnosis' | 'treatment' | 'complaint' | 'history' | 'allergy';

// ═══════════════════════════════════════════════════════════════════════════════
// SISTEMAS ANATÓMICOS (para organización espacial)
// ═══════════════════════════════════════════════════════════════════════════════

export type AnatomicalSystem = 'cardiovascular' | 'respiratory' | 'neurological' | 'gastrointestinal' | 'renal' | 'metabolic' | 'infectious' | 'musculoskeletal' | 'endocrine' | 'hematologic';

// ═══════════════════════════════════════════════════════════════════════════════
// ESTRUCTURAS DE DATOS CORE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Concepto médico codificado (SNOMED CT, LOINC, ICD-10)
 */
export interface MedicalConcept {
  code: string;                       // Código del sistema
  display: string;                    // Nombre legible
  system: string;                     // URI del sistema (http://snomed.info/sct, etc.)
}

/**
 * Cantidad medida con unidades
 */
export interface Quantity {
  value: number;
  unit: string;
  comparator?: '<' | '>' | '<=' | '>=';
}

/**
 * Rango de referencia normal
 */
export interface ReferenceRange {
  low?: number;
  high?: number;
  text?: string;
}

/**
 * Propiedades físicas para simulación del grafo 3D
 */
export interface PhysicsProperties {
  mass: number;                       // Masa del nodo (Labs=10, Síntomas=3)
  volatility: number;                 // Qué tan "flotante" es (0-1)
  timeCriticality: number;            // Urgencia temporal (0-1)
  decayHalfLife?: number;             // Vida media para medicamentos (segundos simulados)
}

/**
 * Configuración visual del nodo
 */
export interface NodeVisualization {
  baseColor: string;                  // Color hex base
  priorityColor?: string;             // Color de triage si aplica
  icon?: string;                      // Emoji o referencia
  pulse?: boolean;                    // Si debe tener animación de pulso
  glowIntensity?: number;             // Intensidad de brillo (0-1)
  scale?: number;                     // Escala adicional
}

/**
 * Relación entre nodos clínicos
 */
export interface ClinicalRelationship {
  targetId: string;                   // ID del nodo destino
  type: RelationshipType;
  strength: number;                   // 0-1, correlación estadística
  bidirectional: boolean;
  evidence?: string;                  // Referencia bibliográfica/evidencia
}

export type RelationshipType = 
  | 'causes'           // Causa -> Efecto
  | 'indicates'        // Indica/sugiere
  | 'contraindicates'  // Contraindica
  | 'treats'           // Trata/cura
  | 'associated-with'  // Asociación estadística
  | 'temporal-sequence'; // Secuencia temporal

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFAZ PRINCIPAL: NODO CLÍNICO
// ═══════════════════════════════════════════════════════════════════════════════

export interface ClinicalNodeData {
  id: string;
  type: NodeType;
  concept: MedicalConcept;
  system?: AnatomicalSystem;          // Sistema anatómico asociado

  // Valores y medidas
  valueQuantity?: Quantity;
  valueString?: string;
  valueBoolean?: boolean;
  valueCodeableConcept?: MedicalConcept;

  // Referencias normales
  referenceRange?: ReferenceRange;

  // Metadatos clínicos
  clinicalStatus: 'active' | 'resolved' | 'inactive' | 'recurrence' | 'remission';
  verificationStatus?: 'unconfirmed' | 'provisional' | 'differential' | 'confirmed' | 'refuted' | 'entered-in-error';
  severity?: 'mild' | 'moderate' | 'severe' | 'life-threatening';
  certainty?: number;                 // 0-1, certeza del dato

  // Temporal
  onsetDateTime?: string;              // ISO 8601
  recordedDate?: string;
  abatementDateTime?: string;

  // Física y visualización
  physics: PhysicsProperties;
  visualization: NodeVisualization;

  // Relaciones
  relationships: ClinicalRelationship[];

  // Metadatos adicionales
  note?: string;
  performer?: string;                 // Quién registró/observó
}

// ═══════════════════════════════════════════════════════════════════════════════
// ESTRUCTURA DEL PACIENTE
// ═══════════════════════════════════════════════════════════════════════════════

export interface PatientDemographics {
  age: number;                        // Años
  sex: 'male' | 'female' | 'other';
  weight?: number;                    // kg
  height?: number;                    // cm
  bmi?: number;                       // kg/m² calculado
}

export interface PatientGraph {
  id: string;
  demographics: PatientDemographics;
  triagePriority: TriagePriority;
  chiefComplaint?: string;            // Motivo principal
  nodes: ClinicalNodeData[];
  temporalSnapshots?: PatientGraphSnapshot[];
  lastUpdated: string;
}

export interface PatientGraphSnapshot {
  timestamp: string;
  nodes: ClinicalNodeData[];
  triagePriority: TriagePriority;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETECCIÓN DE PATRONES CLÍNICOS
// ═══════════════════════════════════════════════════════════════════════════════

export interface ClinicalPattern {
  id: string;
  name: string;
  description: string;
  nodeCriteria: PatternCriterion[];   // Criterios de nodos requeridos
  urgency: TriagePriority;
  condition: string;                  // Condición médica sugerida
  confidenceThreshold: number;        // Umbral de confianza para disparar
}

export interface PatternCriterion {
  nodeType?: NodeType;
  conceptCodes?: string[];            // Códigos SNOMED/LOINC
  valuePredicate?: (value: unknown) => boolean;
  severity?: string[];
  required: boolean;
}

export interface DetectedPattern {
  pattern: ClinicalPattern;
  matchedNodes: string[];             // IDs de nodos que coinciden
  confidence: number;                 // 0-1
  detectedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MOTOR DE DIAGNÓSTICO DIFERENCIAL
// ═══════════════════════════════════════════════════════════════════════════════

export interface DifferentialDiagnosis {
  concept: MedicalConcept;
  probability: number;                // Probabilidad bayesiana
  supportingEvidence: string[];       // IDs de nodos a favor
  againstEvidence: string[];          // IDs de nodos en contra
  urgency: TriagePriority;
}

export interface DiagnosticEngineState {
  differentials: DifferentialDiagnosis[];
  leadingDiagnosis: DifferentialDiagnosis | null;
  patternsDetected: DetectedPattern[];
  lastAnalysis: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFACES PARA RENDERIZADO 3D
// ═══════════════════════════════════════════════════════════════════════════════

export interface NodeRenderState {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  acceleration: THREE.Vector3;
  isHovered: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  pulsePhase: number;
  glowIntensity: number;
}

export interface EdgeRenderData {
  sourceId: string;
  targetId: string;
  strength: number;
  color: string;
  dashed: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODO DE TOPOLOGÍA
// ═══════════════════════════════════════════════════════════════════════════════

export type TopologyMode = 'anatomical' | 'fisiopathological' | 'temporal_spiral' | 'differential_competitive';

export interface TopologyConfiguration {
  mode: TopologyMode;
  centerPosition: THREE.Vector3;
  scale: number;
  timeScale: number;
  showLabels: boolean;
  showRelationships: boolean;
  focusSystem?: AnatomicalSystem;
}

// ═══════════════════════════════════════════════════════════════════════════════
// EVENTOS DEL SISTEMA
// ═══════════════════════════════════════════════════════════════════════════════

export type FindingSource = 'initial' | 'exam' | 'click' | 'gesture' | 'timeline';

export type ClinicalEvent =
  | { type: 'NODE_HOVER'; nodeId: string }
  | { type: 'NODE_SELECT'; nodeId: string }
  | { type: 'NODE_EXPAND'; nodeId: string }
  | { type: 'FINDING_REVEALED'; nodeId: string; source: FindingSource }
  | { type: 'EXAM_ORDERED'; examId: string; examLabel: string; nodeIds: string[] }
  | { type: 'HYPOTHESIS_SET'; caseId: string; dxName: string; dxCode: string; probability: number; confirmed: boolean }
  | { type: 'VITAL_CHANGED'; nodeId: string; oldValue: unknown; newValue: unknown; narrative: string; trend: 'worsening' | 'improving' | 'stable' }
  | { type: 'DETERIORATION_STEP'; stepId: string; atMinutes: number; prevented: boolean; narrative: string }
  | { type: 'PATTERN_DETECTED'; pattern: DetectedPattern }
  | { type: 'TRIAGE_CHANGE'; from: TriagePriority; to: TriagePriority }
  | { type: 'DIAGNOSIS_LEAD_CHANGE'; diagnosis: DifferentialDiagnosis }
  | { type: 'TOPOLOGY_CHANGE'; mode: TopologyMode }
  | { type: 'TIME_SCALE_CHANGE'; scale: number }
  | { type: 'EMERGENCY_MODE'; active: boolean }
  | { type: 'QUIZ_START'; caseId: string }
  | { type: 'QUIZ_ANSWER'; questionId: string; correct: boolean }
  | { type: 'QUIZ_COMPLETE'; score: number; maxScore: number }
  | { type: 'TIMELINE_STEP'; stepIndex: number; label: string }
  | { type: 'TUTORIAL_ADVANCE'; stepIndex: number }
  | { type: 'TUTORIAL_COMPLETE' };

// ═══════════════════════════════════════════════════════════════════════════════
// UTILIDADES DE TIPOS
// ═══════════════════════════════════════════════════════════════════════════════

export function isCriticalPriority(priority: TriagePriority): boolean {
  return priority === 'immediate';
}

export function isNodeCritical(node: ClinicalNodeData): boolean {
  if (node.severity === 'life-threatening') return true;
  if (node.physics.timeCriticality > 0.8) return true;
  
  // Verificar valores fuera de rango
  if (node.valueQuantity && node.referenceRange) {
    const val = node.valueQuantity.value;
    if (node.referenceRange.low !== undefined && val < node.referenceRange.low) {
      return true;
    }
    if (node.referenceRange.high !== undefined && val > node.referenceRange.high) {
      return true;
    }
  }
  
  return false;
}
